import {
    cloneBytes,
    cloneGeoPoint,
    cloneTimestamp,
    isBytesLike,
    isFirestoreFieldValue,
    isFirestoreReference,
    isGeoPointLike,
    isTimestampLike,
} from './FirestoreUtils';
import { isArrayLike } from './Lang';

function _set<K extends WeakKey, V>(map: WeakMap<K, V>, key: K, value: V): V {
    map.set(key, value);
    return value;
}

function isExemptFromCopy(x: any) {
    return isFirestoreFieldValue(x) || isFirestoreReference(x);
}

function _deepCopy(
    source: any,
    isTargetingFirestore: boolean,
    memo: WeakMap<any, any>
): any {
    if (source == null) return source;

    switch (typeof source) {
        case 'undefined':
        case 'boolean':
        case 'number':
        case 'string':
        case 'bigint':
            return source;

        case 'function':
            // never copy functions - they can be stateful.
            return _set(memo, source, undefined);

        case 'symbol':
            // unsupported by Firestore, otherwise treat like a primitive
            return isTargetingFirestore ? undefined : source;

        // case "object":
        // handled next
    }

    if (memo.has(source)) {
        return memo.get(source);
    }

    // Note: I used to call MobX's toJS here to strip its instrumentation,
    // but it turns out that's basically a deep clone as well, which we're
    // already doing a more specialized version of.

    if (ArrayBuffer.isView(source)) {
        // There are a few ways you could slice this - this approach is
        // simple and works across realms (iframe boundaries), but it
        // doesn't preserve View->Buffer referential integrity. Which for
        // our purposes doesn't matter because I don't think binary buffers
        // are in use anywhere, but it's worth noting.
        return _set(memo, source, structuredClone(source));
    }

    // Identify objects the old-fashioned way because we could be operating
    // across iframes
    const signature = Object.prototype.toString.call(source);
    switch (signature) {
        case '[object Date]': {
            // Firestore will convert this to Timestamp
            const copy = new Date(source.getTime());
            return _set(memo, source, copy);
        }
        case '[object RegExp]': {
            // unsupported by Firestore, otherwise return a copy
            const copy = isTargetingFirestore ? undefined : new RegExp(source);
            return _set(memo, source, copy);
        }
        case '[object ArrayBuffer]': {
            const ab = source as ArrayBuffer;
            return _set(memo, source, ab.slice(0));
        }

        // note: currently no support for Map or Set
    }

    if (isExemptFromCopy(source)) {
        return _set(memo, source, source);
    }

    if (isTimestampLike(source)) {
        return _set(memo, source, cloneTimestamp(source));
    }

    if (isGeoPointLike(source)) {
        return _set(memo, source, cloneGeoPoint(source));
    }

    if (isBytesLike(source)) {
        return _set(memo, source, cloneBytes(source));
    }

    if (isArrayLike(source)) {
        const copy: any[] = [];
        const length = source.length;

        // for the cycle guard to work, we must associate the copy with the
        // source before trying to copy it, because it may be directly
        // cyclical. e.g. `a = []; a.push(a);`

        memo.set(source, copy);

        // and because we're also supporting ArrayLike, there's little
        // choice but to do an old-fashioned for-loop
        for (let i = 0; i < length; i++) {
            const copiedElement = _deepCopy(
                source[i],
                isTargetingFirestore,
                memo
            );

            // For Arrays bound for Firestore, convert undefined to null to
            // maintain indices. Otherwise just preserve undefined
            if (isTargetingFirestore && copiedElement === undefined) {
                copy[i] = null;
            } else {
                copy[i] = copiedElement;
            }
        }
        return copy;
    }

    {
        const copy: Record<string, any> = {};
        memo.set(source, copy);
        for (const [k, v] of Object.entries(source)) {
            const copiedValue = _deepCopy(v, isTargetingFirestore, memo);

            // For Objects bound for Firestore, drop keys whose values
            // convert to undefined. Otherwise preserve the key-value pair
            if (!isTargetingFirestore || copiedValue !== undefined) {
                copy[k] = copiedValue;
            }
        }
        return copy;
    }
}

export function deepCopyForFirestore(source: any): any {
    const c = _deepCopy(source, true, new WeakMap());
    return c == null ? null : c; // if top level value is undefined, return null instead
}

export function deepCopy(source: any): any {
    return _deepCopy(source, false, new WeakMap());
}
